/**
 * Nguồn sự thật DUY NHẤT cho sơ đồ URL theo ngôn ngữ của qrcode-gen.com.
 *
 *   vi  → https://qrcode-gen.com/...            (root, không tiền tố)
 *   ja  → https://ja.qrcode-gen.com/...         (giữ subdomain vì đang có traffic tốt)
 *   còn lại → https://qrcode-gen.com/<lang>/...  (tiền tố path: /en, /fr, /zh-tw, /id, ...)
 *
 * Subdomain ngôn ngữ cũ (en., fr., ind., zh-tw., ...) → 301 về apex + tiền tố.
 * Module này thuần JS (không import gì) để worker, Next SSR, client và script đều dùng được.
 */

export const APEX_HOST = "qrcode-gen.com";
export const WWW_HOST = "www.qrcode-gen.com";
export const APEX_ORIGIN = `https://${APEX_HOST}`;
export const ROOT_LANGUAGE = "vi";
export const DEFAULT_ALTERNATE_LANGUAGE = "en";

/** Ngôn ngữ vẫn chạy trên subdomain riêng. */
export const SUBDOMAIN_HOST_BY_LANGUAGE = Object.freeze({
  ja: "ja.qrcode-gen.com"
});

export const SUPPORTED_LANGUAGES = Object.freeze([
  "af", "ar", "bn", "de", "en", "es", "fr", "ha", "hi", "id", "it", "ja", "km",
  "ko", "lo", "mm", "ms", "pt", "ru", "sw", "th", "tl", "tr", "ur", "vi", "zh",
  "zh-TW"
]);

const SUPPORTED_LANGUAGE_SET = new Set(SUPPORTED_LANGUAGES);

/** Ngôn ngữ dùng tiền tố path trên apex. */
export const PATH_PREFIX_LANGUAGES = Object.freeze(
  SUPPORTED_LANGUAGES.filter(
    (language) => language !== ROOT_LANGUAGE && !SUBDOMAIN_HOST_BY_LANGUAGE[language]
  )
);

/** Subdomain ngôn ngữ cũ (kể cả alias lịch sử) → mã ngôn ngữ. `id.` KHÔNG nằm đây vì là hệ đăng nhập. */
const LEGACY_SUBDOMAIN_LANGUAGE = (() => {
  const map = {};
  PATH_PREFIX_LANGUAGES.forEach((language) => {
    if (language === "id") return;
    map[toPathSegment(language)] = language;
  });
  map.ind = "id";
  return Object.freeze(map);
})();

const ASSET_PATH_PREFIXES = ["/_next/", "/api/", "/media/", "/images/", "/fonts/", "/js/", "/static/"];

export function normalizeLanguageCode(language) {
  const raw = String(language || "").trim();
  if (!raw) return "";
  const lowered = raw.toLowerCase().replace(/_/g, "-");
  if (lowered === "vn") return "vi";
  if (lowered === "ind") return "id";
  if (lowered === "zh-tw") return "zh-TW";
  return lowered;
}

export function isSupportedLanguage(language) {
  return SUPPORTED_LANGUAGE_SET.has(normalizeLanguageCode(language));
}

/** Đoạn path tiền tố (chữ thường) của ngôn ngữ: en → "en", zh-TW → "zh-tw", vi/ja → "". */
export function toPathSegment(language) {
  const normalized = normalizeLanguageCode(language);
  if (!normalized || normalized === ROOT_LANGUAGE || SUBDOMAIN_HOST_BY_LANGUAGE[normalized]) return "";
  return normalized.toLowerCase();
}

/** "/en", "/zh-tw" hoặc "" cho vi/ja. */
export function getLanguagePathPrefix(language) {
  const segment = toPathSegment(language);
  return segment ? `/${segment}` : "";
}

export function getLanguageHost(language) {
  const normalized = normalizeLanguageCode(language);
  return SUBDOMAIN_HOST_BY_LANGUAGE[normalized] || APEX_HOST;
}

export function getLanguageOrigin(language) {
  return `https://${getLanguageHost(language)}`;
}

/** origin + tiền tố, KHÔNG có "/" cuối: https://qrcode-gen.com/en | https://qrcode-gen.com | https://ja.qrcode-gen.com */
export function getLanguageBaseUrl(language) {
  return `${getLanguageOrigin(language)}${getLanguagePathPrefix(language)}`;
}

export function normalizePathname(pathname) {
  const raw = String(pathname || "/").split(/[?#]/)[0];
  return raw.startsWith("/") ? raw : `/${raw}`;
}

/** Ghép tiền tố vào path tương đối gốc ("/" → "/en/", "/blog" → "/en/blog"). Không đụng path đã có tiền tố hoặc asset. */
export function applyPathPrefix(pathname, prefix) {
  const path = normalizePathname(pathname);
  if (!prefix) return path;
  if (path === prefix || path.startsWith(`${prefix}/`)) return path;
  if (isAssetLikePath(path)) return path;
  return path === "/" ? `${prefix}/` : `${prefix}${path}`;
}

export function localizePathname(pathname, language) {
  return applyPathPrefix(pathname, getLanguagePathPrefix(language));
}

/** URL tuyệt đối canonical của một path cho ngôn ngữ (search phải bắt đầu bằng "?" nếu có). */
export function buildLanguageUrl(language, pathname = "/", search = "") {
  const base = getLanguageBaseUrl(language);
  const path = normalizePathname(pathname);
  return `${base}${path}${search || ""}`;
}

export function isAssetLikePath(pathname) {
  const path = normalizePathname(pathname);
  if (ASSET_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) return true;
  if (path === "/api" || path === "/media") return true;
  const lastSegment = path.split("/").pop() || "";
  return /\.[a-z0-9]{1,8}$/i.test(lastSegment);
}

/**
 * Tách tiền tố ngôn ngữ ở đầu path.
 * @returns {{ language: string, segment: string, prefix: string, pathname: string }}
 *   language: mã đã chuẩn hoá (kể cả vi/ja nếu ai đó gõ /vi/, /ja/) hoặc "" nếu không có tiền tố.
 *   segment: đoạn gốc trong URL (để phát hiện sai hoa/thường hoặc alias như /ind, /zh-TW).
 *   pathname: phần còn lại, luôn bắt đầu bằng "/".
 */
export function parseLanguagePath(pathname) {
  const path = normalizePathname(pathname);
  const match = path.match(/^\/([A-Za-z]{2,3}(?:-[A-Za-z]{2})?)(?=\/|$)/);
  if (!match) return { language: "", segment: "", prefix: "", pathname: path };
  const segment = match[1];
  const language = normalizeLanguageCode(segment);
  if (!SUPPORTED_LANGUAGE_SET.has(language)) {
    return { language: "", segment: "", prefix: "", pathname: path };
  }
  const rest = path.slice(match[0].length) || "/";
  return { language, segment, prefix: `/${segment}`, pathname: rest };
}

export function stripLanguagePrefix(pathname) {
  return parseLanguagePath(pathname).pathname;
}

export function normalizeHost(hostname) {
  return String(hostname || "").trim().toLowerCase().replace(/:\d+$/, "");
}

export function isLocalHost(hostname) {
  const host = normalizeHost(hostname);
  return (
    !host ||
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host.endsWith(".localhost") ||
    host.endsWith(".workers.dev") ||
    host.endsWith(".pages.dev")
  );
}

export function isProductionHost(hostname) {
  const host = normalizeHost(hostname);
  return host === APEX_HOST || host.endsWith(`.${APEX_HOST}`);
}

/**
 * Phân loại host.
 * @returns {{ kind: "apex"|"www"|"subdomain"|"legacy"|"local"|"other", language: string }}
 */
export function classifyHost(hostname) {
  const host = normalizeHost(hostname);
  if (host === APEX_HOST) return { kind: "apex", language: "" };
  if (host === WWW_HOST) return { kind: "www", language: "" };
  const subdomainEntry = Object.entries(SUBDOMAIN_HOST_BY_LANGUAGE).find(([, h]) => h === host);
  if (subdomainEntry) return { kind: "subdomain", language: subdomainEntry[0] };
  if (host.endsWith(`.${APEX_HOST}`)) {
    const sub = host.slice(0, -(APEX_HOST.length + 1));
    const legacyLanguage = LEGACY_SUBDOMAIN_LANGUAGE[sub];
    if (legacyLanguage) return { kind: "legacy", language: legacyLanguage };
    return { kind: "other", language: "" };
  }
  if (isLocalHost(host)) return { kind: "local", language: "" };
  return { kind: "other", language: "" };
}

/**
 * Suy ra ngôn ngữ + tiền tố + path thật cho một request, kèm URL cần 301 (nếu URL chưa chuẩn).
 *
 * @param {object} params
 * @param {string} params.hostname
 * @param {string} params.pathname
 * @param {string} [params.search] - "?a=b" hoặc ""
 * @param {string} [params.origin] - origin thật của request (dùng cho host local)
 * @param {string} [params.defaultLanguage] - ngôn ngữ khi host local không có tiền tố
 * @returns {{
 *   language: string, prefix: string, pathname: string, search: string,
 *   host: string, hostKind: string, isLocal: boolean,
 *   siteOrigin: string, baseUrl: string, currentUrl: string, redirectTo: string
 * }}
 *   siteOrigin: origin dùng cho link cùng ngôn ngữ (prod: origin canonical; local: origin request)
 *   baseUrl: siteOrigin + prefix (không "/" cuối)
 *   currentUrl: URL chuẩn của chính request (baseUrl + pathname, không search)
 */
export function resolveRequestLocale({
  hostname,
  pathname,
  search = "",
  origin = "",
  defaultLanguage = ROOT_LANGUAGE
} = {}) {
  const host = normalizeHost(hostname);
  const { kind, language: hostLanguage } = classifyHost(host);
  const isLocal = kind === "local" || kind === "other";
  const parsed = parseLanguagePath(pathname);
  const safeSearch = search && !search.startsWith("?") ? `?${search}` : search || "";
  const fallbackLanguage = isSupportedLanguage(defaultLanguage)
    ? normalizeLanguageCode(defaultLanguage)
    : ROOT_LANGUAGE;

  const finish = (language, prefix, restPath, redirectTo = "") => {
    const restParsed = parseLanguagePath(restPath);
    // /en/fr/blog → gộp về một tiền tố (giữ tiền tố đầu, bỏ tiền tố lặp)
    if (restParsed.language && !redirectTo) {
      const collapsed = restParsed.pathname;
      const collapsedBase = isLocal && origin ? `${origin}${prefix}` : getLanguageBaseUrl(language);
      return finish(language, prefix, collapsed, `${collapsedBase}${collapsed}${safeSearch}`);
    }
    const siteOrigin =
      isLocal && origin ? origin : getLanguageOrigin(language);
    const baseUrl = `${siteOrigin}${prefix}`;
    return {
      language,
      prefix,
      pathname: restPath,
      search: safeSearch,
      host,
      hostKind: kind,
      isLocal,
      siteOrigin,
      baseUrl,
      currentUrl: `${baseUrl}${restPath}`,
      redirectTo
    };
  };

  // 1) Subdomain ngôn ngữ cũ → apex + tiền tố (giữ nguyên path/search)
  if (kind === "legacy") {
    const restPath = parsed.pathname;
    const targetLanguage = parsed.language && parsed.language !== hostLanguage
      ? parsed.language
      : hostLanguage;
    return finish(
      targetLanguage,
      getLanguagePathPrefix(targetLanguage),
      restPath,
      buildLanguageUrl(targetLanguage, restPath, safeSearch)
    );
  }

  // 2) www → apex
  if (kind === "www") {
    const language = parsed.language || ROOT_LANGUAGE;
    const target = parsed.language
      ? buildLanguageUrl(parsed.language, parsed.pathname, safeSearch)
      : `${APEX_ORIGIN}${parsed.pathname}${safeSearch}`;
    return finish(language, getLanguagePathPrefix(language), parsed.pathname, target);
  }

  // 3) Subdomain còn giữ (ja.)
  if (kind === "subdomain") {
    if (parsed.language && parsed.language !== hostLanguage) {
      // ja.qrcode-gen.com/en/foo → https://qrcode-gen.com/en/foo
      return finish(
        parsed.language,
        getLanguagePathPrefix(parsed.language),
        parsed.pathname,
        buildLanguageUrl(parsed.language, parsed.pathname, safeSearch)
      );
    }
    if (parsed.language === hostLanguage) {
      // ja.qrcode-gen.com/ja/foo → ja.qrcode-gen.com/foo
      return finish(
        hostLanguage,
        "",
        parsed.pathname,
        `${getLanguageOrigin(hostLanguage)}${parsed.pathname}${safeSearch}`
      );
    }
    return finish(hostLanguage, "", parsed.pathname);
  }

  // 4) apex hoặc host local/khác (coi như apex)
  const requestOrigin = isLocal && origin ? origin : APEX_ORIGIN;
  if (!parsed.language) {
    const language = kind === "apex" ? ROOT_LANGUAGE : fallbackLanguage;
    // Host local có DEFAULT_LANGUAGE là ngôn ngữ tiền tố: phục vụ ở root để dev không cần gõ tiền tố.
    return finish(language, "", parsed.pathname);
  }

  const language = parsed.language;
  if (language === ROOT_LANGUAGE) {
    // /vi/foo → /foo
    return finish(language, "", parsed.pathname, `${requestOrigin}${parsed.pathname}${safeSearch}`);
  }
  if (SUBDOMAIN_HOST_BY_LANGUAGE[language]) {
    // /ja/foo → https://ja.qrcode-gen.com/foo
    return finish(language, "", parsed.pathname, buildLanguageUrl(language, parsed.pathname, safeSearch));
  }

  const canonicalPrefix = getLanguagePathPrefix(language);
  const canonicalRest = parsed.pathname;
  const rawPath = normalizePathname(pathname);
  const canonicalPath = `${canonicalPrefix}${canonicalRest}`;
  let redirectTo = "";
  if (rawPath !== canonicalPath) {
    // /EN/blog, /ind/blog, /zh-TW/blog, /en (thiếu "/" cuối) → dạng chuẩn
    redirectTo = `${requestOrigin}${canonicalPath}${safeSearch}`;
  }
  return finish(language, canonicalPrefix, canonicalRest, redirectTo);
}

/**
 * Thêm tiền tố ngôn ngữ vào mọi href tương đối gốc trong một đoạn HTML do server render.
 * Bỏ qua asset, /api, /media, href đã có tiền tố và URL tuyệt đối.
 */
export function prefixRootRelativeHrefs(html, prefix) {
  if (!prefix || !html) return html;
  return String(html).replace(/\b(href|action)=(["'])(\/[^"'\s>]*)\2/gi, (match, attr, quote, value) => {
    if (value.startsWith("//")) return match;
    const [pathPart, ...rest] = value.split(/(?=[?#])/);
    const localized = applyPathPrefix(pathPart, prefix);
    if (localized === pathPart) return match;
    return `${attr}=${quote}${localized}${rest.join("")}${quote}`;
  });
}

/** Danh sách host worker phải nhận (route Cloudflare) — apex, www, subdomain giữ + subdomain cũ để 301. */
export function getAllServedHosts() {
  const hosts = new Set([APEX_HOST, WWW_HOST, ...Object.values(SUBDOMAIN_HOST_BY_LANGUAGE)]);
  Object.keys(LEGACY_SUBDOMAIN_LANGUAGE).forEach((sub) => hosts.add(`${sub}.${APEX_HOST}`));
  return Array.from(hosts);
}
